// src/pages/customer-panel/support/SupportTicketsPage.js
// Support Tickets Listing Page

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    TextField,
    IconButton,
    Chip,
    Button,
    CircularProgress,
    Alert,
    InputAdornment,
    Tooltip,
    Tabs,
    Tab,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddIcon from '@mui/icons-material/Add';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import axiosInstance from '../../../utils/axiosInstance';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const GREEN_ACCENT = '#10b981';

const STATUS_CONFIG = {
    OPEN:             { color: '#0ea5e9', bg: '#e0f2fe', label: 'Open' },
    IN_PROGRESS:      { color: '#f59e0b', bg: '#fef3c7', label: 'In Progress' },
    RESOLVED:         { color: '#10b981', bg: '#d1fae5', label: 'Resolved' },
    CLOSED:           { color: '#6b7280', bg: '#f3f4f6', label: 'Closed' },
    ESCALATED:        { color: '#ef4444', bg: '#fee2e2', label: 'Escalated' },
    PENDING_CUSTOMER: { color: '#8b5cf6', bg: '#ede9fe', label: 'Pending Customer' },
};

const PRIORITY_CONFIG = {
    LOW:      { color: '#6b7280', bg: '#f3f4f6', label: 'Low' },
    MEDIUM:   { color: '#f59e0b', bg: '#fef3c7', label: 'Medium' },
    HIGH:     { color: '#ef4444', bg: '#fee2e2', label: 'High' },
    CRITICAL: { color: '#dc2626', bg: '#fecaca', label: 'Critical' },
};

const STATUS_TABS = [
    { value: 'all',         label: 'All' },
    { value: 'OPEN',        label: 'Open' },
    { value: 'IN_PROGRESS', label: 'In Progress' },
    { value: 'RESOLVED',    label: 'Resolved' },
    { value: 'CLOSED',      label: 'Closed' },
];

export default function SupportTicketsPage() {
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const [tickets, setTickets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalCount, setTotalCount] = useState(0);

    const fetchTickets = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const params = {
                search,
                page: page + 1,
                limit: rowsPerPage,
            };
            if (statusFilter !== 'all') {
                params.status = statusFilter;
            }
            const response = await axiosInstance.get('/api/customer/tickets', { params });
            const data = response.data;
            setTickets(data.data || data.tickets || []);
            setTotalCount(data.total || data.totalCount || 0);
        } catch (err) {
            console.error('Failed to fetch tickets:', err);
            setError(err.response?.data?.message || 'Failed to load support tickets.');
        } finally {
            setLoading(false);
        }
    }, [search, statusFilter, page, rowsPerPage]);

    useEffect(() => {
        document.title = 'Support Tickets | Customer Panel';
        fetchTickets();
    }, [fetchTickets]);

    const formatDate = (dateStr) => {
        if (!dateStr) return '--';
        try {
            return new Date(dateStr).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
            });
        } catch {
            return '--';
        }
    };

    const getStatusChip = (status) => {
        const config = STATUS_CONFIG[status] || STATUS_CONFIG.OPEN;
        return (
            <Chip
                label={config.label}
                size="small"
                sx={{
                    fontFamily,
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    bgcolor: config.bg,
                    color: config.color,
                }}
            />
        );
    };

    const getPriorityChip = (priority) => {
        const config = PRIORITY_CONFIG[priority] || PRIORITY_CONFIG.LOW;
        return (
            <Chip
                label={config.label}
                size="small"
                sx={{
                    fontFamily,
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    bgcolor: config.bg,
                    color: config.color,
                }}
            />
        );
    };

    const handleRowClick = (ticket) => {
        const id = ticket.id || ticket._id;
        navigate(`/customer-panel/support/${id}`);
    };

    // Mobile card view
    const renderMobileCards = () => {
        if (loading) {
            return (
                <Box sx={{ textAlign: 'center', py: 6 }}>
                    <CircularProgress size={32} sx={{ color: GREEN_ACCENT }} />
                </Box>
            );
        }
        if (tickets.length === 0) {
            return (
                <Box sx={{ textAlign: 'center', py: 6 }}>
                    <SupportAgentIcon sx={{ fontSize: 48, color: '#e2e8f0', mb: 1 }} />
                    <Typography sx={{ fontFamily, color: '#94a3b8' }}>No tickets found</Typography>
                </Box>
            );
        }
        return tickets.map((ticket) => (
            <Paper
                key={ticket.id || ticket._id}
                onClick={() => handleRowClick(ticket)}
                sx={{
                    p: 2,
                    mb: 1.5,
                    borderRadius: '12px',
                    border: '1px solid',
                    borderColor: 'grey.100',
                    cursor: 'pointer',
                    '&:hover': { bgcolor: '#f8fafc' },
                }}
            >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                    <Typography sx={{ fontFamily, fontWeight: 700, color: GREEN_ACCENT, fontSize: '0.85rem' }}>
                        {ticket.ticket_number || '--'}
                    </Typography>
                    {getStatusChip(ticket.status)}
                </Box>
                <Typography sx={{ fontFamily, fontWeight: 600, color: '#1e293b', fontSize: '0.95rem', mb: 0.5 }}>
                    {ticket.subject || '--'}
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                    {getPriorityChip(ticket.priority)}
                    <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.8rem' }}>
                        {formatDate(ticket.created_at)}
                    </Typography>
                </Box>
            </Paper>
        ));
    };

    // Desktop table view
    const renderTable = () => (
        <TableContainer>
            <Table>
                <TableHead>
                    <TableRow sx={{ bgcolor: '#f8fafc' }}>
                        {['Ticket #', 'Subject', 'Priority', 'Status', 'Created', ''].map((header) => (
                            <TableCell
                                key={header}
                                sx={{
                                    fontFamily,
                                    fontWeight: 700,
                                    color: '#64748b',
                                    fontSize: '0.75rem',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px',
                                }}
                            >
                                {header}
                            </TableCell>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {loading ? (
                        <TableRow>
                            <TableCell colSpan={6} sx={{ textAlign: 'center', py: 6 }}>
                                <CircularProgress size={32} sx={{ color: GREEN_ACCENT }} />
                            </TableCell>
                        </TableRow>
                    ) : tickets.length === 0 ? (
                        <TableRow>
                            <TableCell colSpan={6} sx={{ textAlign: 'center', py: 6 }}>
                                <SupportAgentIcon sx={{ fontSize: 48, color: '#e2e8f0', mb: 1, display: 'block', mx: 'auto' }} />
                                <Typography sx={{ fontFamily, color: '#94a3b8' }}>No tickets found</Typography>
                            </TableCell>
                        </TableRow>
                    ) : (
                        tickets.map((ticket) => (
                            <TableRow
                                key={ticket.id || ticket._id}
                                onClick={() => handleRowClick(ticket)}
                                sx={{
                                    cursor: 'pointer',
                                    '&:hover': { bgcolor: '#f8fafc' },
                                }}
                            >
                                <TableCell sx={{ fontFamily, fontWeight: 600, color: GREEN_ACCENT }}>
                                    {ticket.ticket_number || '--'}
                                </TableCell>
                                <TableCell sx={{ fontFamily, fontWeight: 600, color: '#1e293b', maxWidth: 300 }}>
                                    <Typography noWrap sx={{ fontFamily, fontWeight: 600, color: '#1e293b' }}>
                                        {ticket.subject || '--'}
                                    </Typography>
                                </TableCell>
                                <TableCell>
                                    {getPriorityChip(ticket.priority)}
                                </TableCell>
                                <TableCell>
                                    {getStatusChip(ticket.status)}
                                </TableCell>
                                <TableCell sx={{ fontFamily, color: '#64748b', fontSize: '0.85rem' }}>
                                    {formatDate(ticket.created_at)}
                                </TableCell>
                                <TableCell>
                                    <IconButton size="small" sx={{ color: '#94a3b8' }}>
                                        <ArrowForwardIcon fontSize="small" />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        ))
                    )}
                </TableBody>
            </Table>
        </TableContainer>
    );

    return (
        <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1200, mx: 'auto' }}>
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
                <Box>
                    <Typography variant="h4" sx={{ fontFamily, fontWeight: 700, color: '#1e293b', fontSize: { xs: '1.5rem', md: '2rem' } }}>
                        Support Tickets
                    </Typography>
                    <Typography variant="body2" sx={{ fontFamily, color: '#64748b', mt: 0.5 }}>
                        View and manage your support requests
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <IconButton onClick={fetchTickets} sx={{ color: '#64748b' }}>
                        <RefreshIcon />
                    </IconButton>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => navigate('/customer-panel/support/new')}
                        sx={{
                            fontFamily,
                            fontWeight: 600,
                            textTransform: 'none',
                            borderRadius: '10px',
                            bgcolor: GREEN_ACCENT,
                            '&:hover': { bgcolor: '#059669' },
                            px: 3,
                        }}
                    >
                        Create Ticket
                    </Button>
                </Box>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 3, borderRadius: '12px', fontFamily }}>
                    {error}
                </Alert>
            )}

            <Paper
                sx={{
                    borderRadius: '16px',
                    border: '1px solid',
                    borderColor: 'grey.100',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                    overflow: 'hidden',
                }}
            >
                {/* Status Tabs */}
                <Tabs
                    value={statusFilter}
                    onChange={(e, newVal) => { setStatusFilter(newVal); setPage(0); }}
                    variant={isMobile ? 'scrollable' : 'standard'}
                    scrollButtons={isMobile ? 'auto' : false}
                    sx={{
                        px: 2,
                        borderBottom: '1px solid',
                        borderColor: 'grey.100',
                        '& .MuiTab-root': {
                            fontFamily,
                            fontWeight: 600,
                            textTransform: 'none',
                            minHeight: 48,
                        },
                        '& .Mui-selected': { color: GREEN_ACCENT },
                        '& .MuiTabs-indicator': { bgcolor: GREEN_ACCENT },
                    }}
                >
                    {STATUS_TABS.map((tab) => (
                        <Tab key={tab.value} label={tab.label} value={tab.value} />
                    ))}
                </Tabs>

                {/* Search */}
                <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2, borderBottom: '1px solid', borderColor: 'grey.100' }}>
                    <TextField
                        placeholder="Search by subject or ticket number..."
                        size="small"
                        value={search}
                        onChange={(e) => { setSearch(e.target.value); setPage(0); }}
                        sx={{
                            flex: 1,
                            maxWidth: 400,
                            '& .MuiInputBase-root': { fontFamily, borderRadius: '10px' },
                        }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon sx={{ color: '#94a3b8' }} />
                                </InputAdornment>
                            ),
                        }}
                    />
                </Box>

                {/* Content: mobile cards or desktop table */}
                {isMobile ? (
                    <Box sx={{ p: 2 }}>
                        {renderMobileCards()}
                    </Box>
                ) : (
                    renderTable()
                )}

                <TablePagination
                    component="div"
                    count={totalCount}
                    page={page}
                    onPageChange={(e, newPage) => setPage(newPage)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                    sx={{ '& *': { fontFamily } }}
                />
            </Paper>
        </Box>
    );
}
